import React from 'react';
import type { PressableProps } from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import type { AddressType } from '@sweetgreen/garnish';
import {
  BREAKPOINTS,
  theme,
  useActiveAnimation,
  useBreakpointHelpers,
  useFluidSize,
  usePressableState,
  VStack,
  webOnlyStyles,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useOpenBag } from '../../../../components/Bag/Bag.hooks';
import { useOrderStatusNavigation } from '../../../OrderStatusScreen/OrderStatus.navigation';
import { useOrderStatusTelemetry } from '../../../OrderStatusScreen/OrderStatus.telemetry';
import { Greetings } from '../Greetings';
import { Callout } from '../OrderingActionCard';
import type { GetOrderStatusQuery } from './OrderStatus.query.generated';
import { useI18nValues } from './useI18nValues';

export const WelcomePanePure = React.memo((props: WelcomePanePureProps) => {
  const {
    isLoggedIn,
    data,
    isLoading,
    cartOrderType,
    hasCart,
    seasonalHeroHeadlineText,
    orderPendingFeedbackId,
  } = props;
  const { t } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();
  const { i18nValues, params } = useI18nValues({
    isLoggedIn,
    data,
    hasCart,
    cartOrderType,
    isLoading,
    orderPendingFeedbackId,
  });
  const fluid = useFluidSize({
    min: BREAKPOINTS[1].MIN,
    max: BREAKPOINTS[3].MIN,
  });
  const contentSpacing = fluid(24, 40);

  const openBag = useOpenBag({ shouldUpdateCart: true });
  const { handleOnRateOrder, handleOrderStatus } = useOrderStatusNavigation();
  const { handleOrderStatusEvent } = useOrderStatusTelemetry();

  const orderId = params?.orderId;
  const hasStatusId = Boolean(orderId);
  const shouldShowBagCard = params.hasCart && !isMobile;
  const shouldShowStatusCard = !params.hasCart && isLoggedIn && hasStatusId;
  const greeting = t('home.welcome.greeting', i18nValues);
  const heroHeadline = params.showSeasonalMessaging
    ? seasonalHeroHeadlineText
    : t('home.welcome.heroHeadline', i18nValues);

  const showStatusCard = Boolean(
    shouldShowStatusCard && orderId && !params.showSeasonalMessaging,
  );

  const shouldShowCallToAction = shouldShowBagCard || showStatusCard;

  const isPendingFeedbackStatus =
    (i18nValues.orderStatusWithExtraTypes as string) === 'feedback';

  const handleOnPress = React.useCallback(() => {
    if (shouldShowBagCard) {
      openBag();

      return;
    }

    if (isPendingFeedbackStatus) handleOnRateOrder(orderId);
    else {
      handleOrderStatus(orderId);
      handleOrderStatusEvent('home.cta');
    }
  }, [
    isPendingFeedbackStatus,
    shouldShowBagCard,
    orderId,
    handleOnRateOrder,
    handleOrderStatus,
    handleOrderStatusEvent,
    openBag,
  ]);

  return (
    <VStack customSpacing={contentSpacing} {...props}>
      <Greetings
        testID={
          params.customerName
            ? 'home.welcome.greetings-logged-in'
            : 'home.welcome.greetings'
        }
        greeting={greeting}
        heroHeadline={heroHeadline}
      />
      {shouldShowCallToAction ? (
        <WelcomePaneCta
          isLoading={isLoading}
          isPendingFeedbackStatus={isPendingFeedbackStatus}
          shouldShowBagCard={shouldShowBagCard}
          shouldShowStatusCard={showStatusCard}
          cartOrderType={cartOrderType}
          i18nValues={i18nValues}
          onPress={handleOnPress}
        />
      ) : null}
    </VStack>
  );
});

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const WelcomePaneCta = (props: WelcomePaneCtaProps & PressableProps) => {
  const {
    isPendingFeedbackStatus,
    isLoading,
    shouldShowStatusCard,
    shouldShowBagCard,
    i18nValues,
    cartOrderType,
    ...pressableProps
  } = props;

  const pressableRef = React.useRef(null);

  const { isInteracting } = usePressableState(pressableRef);
  const { t } = useLocalizationContext();

  // ─────────────────────────────────────────────────────────────────

  const title = shouldShowBagCard
    ? t('home.welcome.bag-cta.title', { orderType: cartOrderType })
    : t('home.welcome.cta.orderStatusHeadline', i18nValues);
  const subtitle = shouldShowStatusCard
    ? t('home.welcome.cta.orderStatusBody', i18nValues)
    : '';
  const a11yHint = shouldShowBagCard
    ? t('home.welcome.bag-cta.title', { orderType: cartOrderType })
    : t('home.welcome.cta.a11yHint', i18nValues);

  // ─────────────────────────────────────────────────────────────────

  const pressableAnimatedStyle = useActiveAnimation(
    isInteracting,
    isPendingFeedbackStatus
      ? theme.colors.OPACITY.TRANSPARENT
      : theme.colors.KALE,
    isPendingFeedbackStatus
      ? theme.colors.OPACITY.GREEN_2.LIGHTER
      : theme.colors.SPINACH,
  );
  const pressableStyles = [
    styles.pressable,
    webOnlyStyles({
      outlineColor: theme.colors.GRAY,
      outlineOffset: theme.spacing.small,
    }),
  ];
  const cardStyles = [styles.card, pressableAnimatedStyle];

  // ─────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      testID={isLoading ? '' : 'home.dynamic-cta'}
      style={cardStyles}
    >
      <Pressable
        style={pressableStyles}
        testID="home.dynamic-Callout"
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityHint={a11yHint}
        ref={pressableRef}
        {...pressableProps}
      >
        <Callout
          testID="home.welcome.cta-card"
          title={title}
          subtitle={subtitle}
          palette={isPendingFeedbackStatus ? 'secondary' : 'primary'}
        />
      </Pressable>
    </Animated.View>
  );
};

// ─── STYLES ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    borderRadius: theme.radius.large,
  },
  pressable: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type WelcomePanePureProps = {
  readonly isLoggedIn: boolean;
  readonly data?: GetOrderStatusQuery;
  readonly isLoading: boolean;
  readonly hasCart: boolean;
  readonly cartOrderType: AddressType;
  readonly seasonalHeroHeadlineText: string;
  readonly orderPendingFeedbackId?: string;
};

type WelcomePaneCtaProps = {
  readonly isLoading: boolean;
  readonly isPendingFeedbackStatus: boolean;
  readonly shouldShowStatusCard: boolean;
  readonly shouldShowBagCard: boolean;
  readonly i18nValues: ReturnType<typeof useI18nValues>['i18nValues'];
  readonly cartOrderType: AddressType;
} & PressableProps;
